import React, { useEffect, useMemo, useState } from "react";
import { X, Check, Plus, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Employee } from "../types/employee";
import api from "../services/api";

interface EmployeeEditData {
  employee: any;
  groups: Array<{ id: number; name: string }>;
  roles: Array<{ id: number; name: string; display_name: string }>;
  selected_services: number[];
  selected_locations: number[];
  business_services: Array<{
    id: number;
    name: string;
    location: { id: number; name: string };
  }>;
  locations: Array<{ id: number; name: string }>;
  shifts: Array<{ id: number; name?: string }>;
  employee_details: any;
}

interface EmployeeSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: Partial<Employee> | null;
  editData?: EmployeeEditData | null;
  loadingEditData?: boolean;
  onSubmit: (data: Partial<Employee>) => void;
  isSaving?: boolean;
}

const EmployeeSidebar: React.FC<EmployeeSidebarProps> = ({
  isOpen,
  onClose,
  initialData,
  editData,
  loadingEditData = false,
  onSubmit,
  isSaving = false,
}) => {
  const [activeTab, setActiveTab] = useState<"profile" | "services">("profile");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [roleId, setRoleId] = useState<number | null>(null);
  const [shiftId, setShiftId] = useState<number | null>(null);
  const [empJoiningDate, setEmpJoiningDate] = useState("");
  const [workType, setWorkType] = useState<"hourly" | "fixed" | "">("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [fixedPrice, setFixedPrice] = useState("");
  const [workingDays, setWorkingDays] = useState("");
  const [commissionPerBooking, setCommissionPerBooking] = useState("");
  const [employeeGroup, setEmployeeGroup] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [mobile, setMobile] = useState("");
  const [callingCode, setCallingCode] = useState("+92");
  const [password, setPassword] = useState("");
  const [additionalField, setAdditionalField] = useState("");
  const [assignedServices, setAssignedServices] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
  const [fetchedRoles, setFetchedRoles] = useState<Array<{ id: number; name: string; display_name: string }>>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [fetchedServices, setFetchedServices] = useState<any[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // Fetch roles and services when sidebar opens
  useEffect(() => {
    const fetchRolesAndServices = async () => {
      // Fetch roles if sidebar is open and editData doesn't have roles
      if (isOpen) {
        // --- Roles Fetching ---
        if (!editData?.roles) {
          setLoadingRoles(true);
          try {
            const response = await api.post("/admin/employees/get-roles");
            // Handle different possible response structures
            let rolesArray = null;
            if (response.data?.roles && Array.isArray(response.data.roles)) {
              rolesArray = response.data.roles;
            } else if (response.data?.data?.roles && Array.isArray(response.data.data.roles)) {
              rolesArray = response.data.data.roles;
            } else if (Array.isArray(response.data)) {
              rolesArray = response.data;
            } else if (response.data?.data && Array.isArray(response.data.data)) {
              rolesArray = response.data.data;
            }

            if (rolesArray && rolesArray.length > 0) {
              const formattedRoles = rolesArray.map((role: any) => ({
                id: role.id,
                name: role.name || "",
                display_name: role.display_name || role.name || ""
              }));
              setFetchedRoles(formattedRoles);
            }
          } catch (err: any) {
            console.error("Error fetching roles:", err.response?.data || err.message);
          } finally {
            setLoadingRoles(false);
          }
        }

        // --- Services Fetching ---
        if (!editData?.business_services || editData.business_services.length === 0) {
          setLoadingServices(true);
          try {
            const response = await api.get("/admin/business-services");
            if (response.data?.success && Array.isArray(response.data.data)) {
              setFetchedServices(response.data.data);
            } else if (Array.isArray(response.data)) {
              // Fallback if response is direct array
              setFetchedServices(response.data);
            }
          } catch (err: any) {
            console.error("Error fetching services:", err);
          } finally {
            setLoadingServices(false);
          }
        }

      } else {
        // Reset when sidebar closes
        setFetchedRoles([]);
        setFetchedServices([]);
      }
    };

    fetchRolesAndServices();
  }, [isOpen, editData?.roles, editData?.business_services]);

  // Get available services from editData or fetched services
  const availableServices = useMemo(() => {
    if (editData?.business_services && editData.business_services.length > 0) {
      return editData.business_services.map((service) => ({
        id: service.id.toString(),
        name: `${service.name} (${service.location?.name || ""})`,
        serviceId: service.id,
      }));
    }

    if (fetchedServices.length > 0) {
      return fetchedServices.map((service) => ({
        id: service.id.toString(),
        name: `${service.name} (${service.location?.name || ""})`,
        serviceId: service.id,
      }));
    }

    return [
      { id: "svc-cut", name: "Haircut", serviceId: 0 },
      { id: "svc-color", name: "Hair Color", serviceId: 0 },
      { id: "svc-nails", name: "Nail Care", serviceId: 0 },
      { id: "svc-massage", name: "Massage", serviceId: 0 },
    ];
  }, [editData, fetchedServices]);

  // Get available roles from editData or fetched roles
  const availableRoles = useMemo(() => {
    if (editData?.roles && editData.roles.length > 0) {
      return editData.roles;
    }
    if (fetchedRoles.length > 0) {
      return fetchedRoles;
    }
    return [];
  }, [editData, fetchedRoles]);

  // Get available shifts from editData
  const availableShifts = useMemo(() => {
    if (editData?.shifts && editData.shifts.length > 0) {
      return editData.shifts;
    }
    return [];
  }, [editData]);

  // Get available groups from editData
  const availableGroups = useMemo(() => {
    if (editData?.groups && editData.groups.length > 0) {
      return editData.groups;
    }
    return [];
  }, [editData]);

  useEffect(() => {
    if (isOpen) {
      // Use editData if available (from API), otherwise use initialData
      const employeeData = editData?.employee || initialData;

      setName(employeeData?.name || "");
      setEmail(employeeData?.email || "");
      setRole(employeeData?.role || "");

      // Set role_id from editData employee_details or initialData
      if (editData?.employee_details?.role_id) {
        setRoleId(editData.employee_details.role_id);
      } else if (initialData?.role_options && Array.isArray(initialData.role_options)) {
        const selectedRole = initialData.role_options.find(r => r.selected);
        if (selectedRole) {
          setRoleId(selectedRole.id);
        }
      }

      // Set shift_id from editData or initialData employeeDetails
      if (editData?.employee_details?.shift_id) {
        setShiftId(editData.employee_details.shift_id);
      } else if ((initialData as any)?.employeeDetails?.shift_id) {
        setShiftId((initialData as any).employeeDetails.shift_id);
      } else {
        setShiftId((initialData as any)?.shift_id || null);
      }

      // Set joining date - check employeeDetails.joining_date from list API response
      const joiningDate = editData?.employee_details?.emp_joining_date ||
        (initialData as any)?.employeeDetails?.joining_date ||
        (initialData as any)?.emp_joining_date ||
        (initialData as any)?.joining_date || "";
      setEmpJoiningDate(joiningDate);

      // Set work type - check employeeDetails.work_type from list API response
      const workTypeValue = editData?.employee_details?.work_type ||
        (initialData as any)?.employeeDetails?.work_type ||
        (initialData as any)?.work_type || "";
      setWorkType(workTypeValue === "hourly" || workTypeValue === "fixed" ? workTypeValue : "");

      // Set work type related fields - check employeeDetails.rate from list API response
      // Rate can be hourly_rate or fixed_price depending on work_type
      const employeeDetails = (initialData as any)?.employeeDetails;
      if (employeeDetails?.rate) {
        if (workTypeValue === "hourly") {
          setHourlyRate(employeeDetails.rate);
        } else if (workTypeValue === "fixed") {
          setFixedPrice(employeeDetails.rate);
        }
      } else {
        // Fallback to editData or other sources
        setHourlyRate(editData?.employee_details?.hourly_rate || (initialData as any)?.hourly_rate || "");
        setFixedPrice(editData?.employee_details?.fixed_price || (initialData as any)?.fixed_price || "");
      }

      // Set working days - check employeeDetails.working_days from list API response
      setWorkingDays(
        editData?.employee_details?.working_days ||
        (initialData as any)?.employeeDetails?.working_days ||
        (initialData as any)?.working_days || ""
      );

      // Set commission - check employeeDetails.commission from list API response
      setCommissionPerBooking(
        editData?.employee_details?.commission_per_booking ||
        editData?.employee_details?.commission ||
        (initialData as any)?.employeeDetails?.commission ||
        (initialData as any)?.commission_per_booking || ""
      );

      // Set employee group from editData or initialData
      if (editData?.employee?.group_id && editData.groups) {
        const group = editData.groups.find(g => g.id === editData.employee.group_id);
        setEmployeeGroup(group?.name || "");
      } else {
        setEmployeeGroup((initialData as any)?.employee_group || initialData?.group || "");
      }

      setImage(null);
      setImagePreview(employeeData?.user_image_url || employeeData?.image || "");
      setMobile(employeeData?.mobile || "");
      setCallingCode(employeeData?.calling_code || "+92");
      setPassword(""); // Password is not pre-filled for security
      setAdditionalField((employeeData as any)?.additional_field || "");

      // Set assigned services from editData selected_services
      if (editData?.selected_services && editData.selected_services.length > 0) {
        setAssignedServices(editData.selected_services.map(id => id.toString()));
      } else if (initialData?.services && Array.isArray(initialData.services)) {
        setAssignedServices(initialData.services.map((s: any) => typeof s === 'string' ? s : String(s)));
      } else {
        setAssignedServices([]);
      }

      // Set selected locations from editData
      if (editData?.selected_locations && editData.selected_locations.length > 0) {
        setSelectedLocations(editData.selected_locations);
      } else if ((initialData as any)?.locations && Array.isArray((initialData as any).locations)) {
        setSelectedLocations((initialData as any).locations);
      } else {
        setSelectedLocations([]);
      }

      setActiveTab("profile");
    } else {
      setName("");
      setEmail("");
      setRole("");
      setRoleId(null);
      setShiftId(null);
      setEmpJoiningDate("");
      setWorkType("");
      setHourlyRate("");
      setFixedPrice("");
      setWorkingDays("");
      setCommissionPerBooking("");
      setEmployeeGroup("");
      setImage(null);
      setImagePreview("");
      setMobile("");
      setCallingCode("+92");
      setPassword("");
      setAdditionalField("");
      setAssignedServices([]);
      setSelectedLocations([]);
    }
  }, [isOpen, initialData, editData]);

  const handleSave = () => {
    const payload: Partial<Employee> & {
      imageFile?: File | null;
      role_id?: number | null;
      shift_id?: number | null;
      emp_joining_date?: string;
      work_type?: string;
      hourly_rate?: string;
      fixed_price?: string;
      working_days?: string;
      commission?: string;
      employee_group?: string;
      password?: string;
      additional_field?: string;
      service_id?: number[];
      location?: number[];
    } = {
      id: initialData?.id,
      name,
      email,
      mobile: mobile || undefined,
      calling_code: callingCode || undefined,
      password: password || undefined,
      additional_field: additionalField || undefined,
      image: imagePreview || undefined,
      service_id: assignedServices.length > 0 ? assignedServices.map(id => parseInt(id)).filter(id => !isNaN(id)) : undefined, // Convert to array of number IDs
      location: selectedLocations.length > 0 ? selectedLocations : undefined,
      imageFile: image, // Pass the File object separately
      role_id: roleId,
      shift_id: shiftId,
      emp_joining_date: empJoiningDate || undefined,
      work_type: workType || undefined,
      hourly_rate: hourlyRate || undefined,
      fixed_price: fixedPrice || undefined,
      working_days: workingDays || undefined,
      commission: commissionPerBooking || undefined,
      employee_group: employeeGroup || undefined,
    };
    onSubmit(payload);
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[99]"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-full lg:w-[60%] bg-white shadow-xl border-l border-gray-200 z-[100] flex flex-col lg:flex-row"
          >
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="absolute -left-0 lg:-left-14 top-4 lg:top-8 w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-md lg:shadow-[-4px_0_12px_rgba(0,0,0,0.15)] hover:bg-[#402B69] hover:text-white transition-all z-[110] group m-4 lg:m-0"
              aria-label="Close"
            >
              <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
            </motion.button>

            <div className="relative h-full w-full flex flex-col lg:flex-row">
              {/* Sidebar Tabs */}
              <div className="relative w-full lg:w-[30%] border-b lg:border-b-0 lg:border-r bg-white pt-16 lg:pt-8">
                <div className="hidden lg:block absolute top-0 right-0 h-full w-[2px] bg-gray-200 shadow-[4px_0_12px_rgba(0,0,0,0.15)]"></div>
                <div className="flex flex-row lg:flex-col text-sm lg:text-[15px] font-bold lg:font-medium overflow-x-auto scrollbar-hide">
                  {[{ id: "profile", label: "Basic Details" }, { id: "services", label: "Services" }].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as "profile" | "services")}
                      className={`relative flex items-center px-6 lg:px-4 py-4 lg:py-2 text-left transition-all duration-200 group whitespace-nowrap ${activeTab === tab.id ? "text-[#885ABB] font-bold lg:font-semibold border-b-2 lg:border-b-0 border-[#885ABB]" : "text-gray-600 lg:text-gray-800 hover:text-[#885ABB]"
                        }`}
                    >
                      <span className={`lg:ml-5 transition-all duration-200 ${activeTab === tab.id ? "translate-x-0" : "lg:group-hover:translate-x-1"}`}>
                        {tab.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Content Area */}
              <div className="flex-1 px-4 lg:px-10 py-6 lg:py-8 overflow-y-auto bg-gray-50 lg:bg-gray-100">
                {activeTab === "profile" && (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-[22px] font-semibold text-[#402B69]">Main details</h2>
                      <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 rounded-md border-[#979797] bg-white text-[#b00808]">Cancel</button>
                        <button
                          onClick={handleSave}
                          disabled={isSaving}
                          className="px-4 py-2 rounded-md bg-[#22c55e] text-white flex items-center gap-2 min-w-[70px] justify-center"
                        >
                          {isSaving ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Saving...</span>
                            </>
                          ) : (
                            "Save"
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      {/* Avatar uploader */}
                      <div className="flex items-center gap-4">
                        <div className="w-20 h-20 rounded-full bg-[#F1ECFB] border border-[#C4A6E1] overflow-hidden flex items-center justify-center text-[#885ABB] text-sm">
                          {imagePreview ? (
                            <img src={imagePreview} alt="Avatar" className="w-full h-full object-cover" />
                          ) : (
                            <span>Avatar</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="px-4 py-2 rounded-md bg-[#F1ECFB] text-[#885ABB] cursor-pointer border border-[#C4A6E1]">
                            Upload image
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files && e.target.files[0];
                                if (!file) return;
                                setImage(file);
                                const reader = new FileReader();
                                reader.onload = () => {
                                  const result = typeof reader.result === "string" ? reader.result : "";
                                  setImagePreview(result);
                                };
                                reader.readAsDataURL(file);
                              }}
                            />
                          </label>
                          {imagePreview && (
                            <button
                              type="button"
                              className="px-3 py-2 rounded-md border text-[#402B69]"
                              onClick={() => {
                                setImage(null);
                                setImagePreview("");
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-[#402B69]">Name</label>
                        <input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Employee name"
                          className="px-3 py-3 border border-[#C4A6E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#885ABB]"
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-[#402B69]">Email</label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Employee email"
                          className="px-3 py-3 border border-[#C4A6E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#885ABB]"
                        />
                      </div>

                      {/* Mobile - After Email */}
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-[#402B69]">Mobile</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={callingCode}
                            onChange={(e) => setCallingCode(e.target.value)}
                            placeholder="+92"
                            className="px-3 py-3 border border-[#C4A6E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#885ABB] w-20"
                          />
                          <input
                            type="text"
                            value={mobile}
                            onChange={(e) => setMobile(e.target.value)}
                            placeholder="Mobile number"
                            className="px-3 py-3 border border-[#C4A6E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#885ABB] flex-1"
                          />
                        </div>
                      </div>

                      {/* Password */}
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-[#402B69]">
                          Password {!initialData?.id && <span className="text-red-500">*</span>}
                        </label>
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder={initialData?.id ? "Leave blank to keep current password" : "Enter password"}
                          required={!initialData?.id}
                          className="px-3 py-3 border border-[#C4A6E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#885ABB]"
                        />
                      </div>

                      {/* Joining Date - Required for both add and edit */}
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-[#402B69]">Joining date <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <input
                            type="date"
                            value={empJoiningDate}
                            onChange={(e) => setEmpJoiningDate(e.target.value)}
                            required
                            className="px-3 py-3 border border-[#C4A6E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#885ABB] w-full"
                          />
                        </div>
                      </div>

                      {/* Work Type - Required, Radio Buttons */}
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-[#402B69]">Work Type <span className="text-red-500">*</span></label>
                        <div className="flex gap-6">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="workType"
                              value="hourly"
                              checked={workType === "hourly"}
                              onChange={(e) => setWorkType(e.target.value as "hourly" | "fixed")}
                              className="w-4 h-4 text-[#885ABB] focus:ring-[#885ABB]"
                            />
                            <span className="text-sm text-[#402B69]">Hourly</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="workType"
                              value="fixed"
                              checked={workType === "fixed"}
                              onChange={(e) => setWorkType(e.target.value as "hourly" | "fixed")}
                              className="w-4 h-4 text-[#885ABB] focus:ring-[#885ABB]"
                            />
                            <span className="text-sm text-[#402B69]">Fixed</span>
                          </label>
                        </div>
                      </div>

                      {/* Conditional Fields based on Work Type */}
                      {workType === "hourly" && (
                        <div className="flex flex-col gap-2">
                          <label className="text-sm font-medium text-[#402B69]">Hourly Rate <span className="text-red-500">*</span></label>
                          <input
                            type="number"
                            step="0.01"
                            value={hourlyRate}
                            onChange={(e) => setHourlyRate(e.target.value)}
                            placeholder="Enter hourly rate"
                            required={workType === "hourly"}
                            className="px-3 py-3 border border-[#C4A6E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#885ABB]"
                          />
                        </div>
                      )}

                      {workType === "fixed" && (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium text-[#402B69]">Fixed Price <span className="text-red-500">*</span></label>
                              <input
                                type="number"
                                step="0.01"
                                value={fixedPrice}
                                onChange={(e) => setFixedPrice(e.target.value)}
                                placeholder="Enter fixed price"
                                required={workType === "fixed"}
                                className="px-3 py-3 border border-[#C4A6E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#885ABB]"
                              />
                            </div>
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium text-[#402B69]">Working Days <span className="text-red-500">*</span></label>
                              <input
                                type="number"
                                value={workingDays}
                                onChange={(e) => setWorkingDays(e.target.value)}
                                placeholder="Enter working days (per month)"
                                required={workType === "fixed"}
                                className="px-3 py-3 border border-[#C4A6E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#885ABB]"
                              />
                            </div>
                          </div>
                        </>
                      )}


                      {/* Assign Role - Required */}
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-[#402B69]">Assign Role <span className="text-red-500">*</span></label>
                        <div className="relative">
                          {loadingRoles ? (
                            <div className="px-3 py-3 border border-[#C4A6E1] rounded-md text-[14px] bg-gray-50 flex items-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#402B69]"></div>
                              <span className="text-gray-500 text-sm">Loading roles...</span>
                            </div>
                          ) : availableRoles.length > 0 ? (
                            <select
                              value={roleId || ""}
                              onChange={(e) => {
                                const selectedId = parseInt(e.target.value);
                                setRoleId(selectedId);
                                const selectedRole = availableRoles.find(r => r.id === selectedId);
                                if (selectedRole) {
                                  setRole(selectedRole.display_name || selectedRole.name);
                                }
                              }}
                              required
                              className="px-3 py-3 border border-[#C4A6E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#885ABB] w-full appearance-none"
                              disabled={loadingEditData}
                            >
                              <option value="">Select Role</option>
                              {availableRoles.map((roleOption) => (
                                <option key={roleOption.id} value={roleOption.id}>
                                  {roleOption.display_name || roleOption.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="px-3 py-3 border border-yellow-300 rounded-md text-[14px] bg-yellow-50">
                              <p className="text-yellow-700 text-sm">
                                {!initialData?.id
                                  ? "Unable to fetch roles. Please check if the API endpoint exists."
                                  : "No roles available"}
                              </p>
                            </div>
                          )}
                          {availableRoles.length > 0 && !loadingRoles && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Commission per booking */}
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-[#402B69]">Commission per booking (%)</label>
                        <input
                          type="number"
                          step="0.1"
                          value={commissionPerBooking}
                          onChange={(e) => setCommissionPerBooking(e.target.value)}
                          placeholder="e.g: 2.5, 5 etc"
                          className="px-3 py-3 border border-[#C4A6E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#885ABB]"
                        />
                      </div>


                    </div>
                  </div>
                )}

                {activeTab === "services" && (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-[22px] font-semibold text-[#402B69]">Assign services</h2>
                      <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 rounded-md border-[#979797] bg-white text-[#b00808]">Cancel</button>
                        <button
                          onClick={handleSave}
                          disabled={isSaving}
                          className="px-4 py-2 rounded-md bg-[#22c55e] text-white flex items-center gap-2 min-w-[70px] justify-center"
                        >
                          {isSaving ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Saving...</span>
                            </>
                          ) : (
                            "Save"
                          )}
                        </button>
                      </div>
                    </div>

                    {loadingEditData ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#402B69] mx-auto mb-4"></div>
                          <p className="text-gray-500">Loading services...</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Selected Services as Tags */}
                        {assignedServices.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {assignedServices.map((serviceId) => {
                              const service = availableServices.find(s => s.id === serviceId);
                              return (
                                <div
                                  key={serviceId}
                                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#885ABB] text-white rounded-md text-sm"
                                >
                                  <span>{service?.name || serviceId}</span>
                                  <button
                                    type="button"
                                    onClick={() => setAssignedServices((prev) => prev.filter((id) => id !== serviceId))}
                                    className="hover:text-red-200 transition-colors"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Available Services List */}
                        {availableServices.length > 0 ? (
                          <div className="grid grid-cols-1 gap-2">
                            {availableServices.map((svc) => {
                              const checked = assignedServices.includes(svc.id);
                              return (
                                <label
                                  key={svc.id}
                                  className={`flex items-center justify-between px-4 py-3 border rounded-md ${checked ? "bg-[#F8F5FF] border-[#C4A6E1]" : "bg-white"}`}
                                >
                                  <span className="text-[15px] text-[#402B69]">{svc.name}</span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setAssignedServices((prev) =>
                                        checked ? prev.filter((id) => id !== svc.id) : [...prev, svc.id]
                                      )
                                    }
                                    className={`w-9 h-9 rounded-md flex items-center justify-center border ${checked ? "bg-[#885ABB] border-[#885ABB] text-white" : "border-[#C4A6E1] text-[#885ABB]"
                                      }`}
                                  >
                                    {checked ? <Check className="w-5 h-5" /> : <Plus className="w-4 h-4" />}
                                  </button>
                                </label>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <p>No services available</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default EmployeeSidebar;
